/**
 * 차년도이관 — 구조 + 마스터(과목/사업)
 * - 과목: 구조 전체 + 과목 전체
 * - 사업: 구조 전체 + 사업 전체 또는 부분선택(상위 자동포함, projectUid 유지)
 */
import {
  BUDGET_TYPE_ALL,
  copyStructureToNextYear,
  loadStructurePayload,
  loadSubjectsByFilter,
  saveSubjectsByFilter,
} from 'budget/budgetStructureStore';
import {
  copyProjectStructureToNextYear,
  loadProjectStructurePayload,
} from 'budget/budgetProjectStructureStore';
import { loadProjects, saveProjects, PROJECT_PURPOSE } from 'budget/budgetProjectStore';
import {
  ensureSubjectsIdentity,
  buildPathCode,
  upsertHistoryForSubjects,
} from 'budget/budgetSubjectHistoryStore';
import {
  ensureProjectsIdentity,
  buildProjectPathCode,
  upsertHistoryForProjects,
} from 'budget/budgetProjectHistoryStore';

export function nextFiscalYear(fiscalYear) {
  return String(Number(fiscalYear) + 1);
}

export function projectRowKey(r) {
  return `${r?.budgetType || ''}::${r?.projectCode || ''}`;
}

/** 선택 사업 + 조상 포함 */
export function expandProjectsWithAncestors(selectedKeys = [], allRows = []) {
  const byKey = new Map((allRows || []).map((r) => [projectRowKey(r), r]));
  const out = new Set(selectedKeys);
  selectedKeys.forEach((key) => {
    let cur = byKey.get(key);
    let guard = 0;
    while (cur && guard++ < 24) {
      out.add(projectRowKey(cur));
      if (!cur.parentCode) break;
      cur = byKey.get(`${cur.budgetType || ''}::${cur.parentCode}`) || null;
    }
  });
  return (allRows || []).filter((r) => out.has(projectRowKey(r)));
}

function rebuildSubjectPaths(rows) {
  const ensured = ensureSubjectsIdentity(rows || []);
  return ensured.map((r) => ({
    ...r,
    pathCode: buildPathCode(r, ensured),
  }));
}

function rebuildProjectPaths(rows) {
  const ensured = ensureProjectsIdentity(rows || []);
  return ensured.map((r) => ({
    ...r,
    pathCode: buildProjectPathCode(r, ensured),
  }));
}

/**
 * 예산과목 차년도이관: 구조 전체 + 과목 전체(사업/자본 포함)
 */
export async function migrateSubjectYear(dims, { userId } = {}) {
  const fy = dims?.fiscalYear;
  if (!fy || !Number.isFinite(Number(fy))) {
    return { ok: false, reason: '회계년도가 올바르지 않습니다.' };
  }
  const sourceStruct = await loadStructurePayload(dims);
  if (!sourceStruct?.rows?.length) {
    return { ok: false, reason: '이관할 과목구조가 없습니다. 먼저 구조를 저장하세요.' };
  }

  const structResult = await copyStructureToNextYear(dims, { userId });
  if (!structResult.ok) return structResult;

  const nextDims = structResult.nextDims;
  const sourceSubjects = rebuildSubjectPaths(
    await loadSubjectsByFilter({ ...dims, budgetType: BUDGET_TYPE_ALL })
  );
  const migrated = rebuildSubjectPaths(sourceSubjects);
  if (migrated.length) {
    const saveResult = await saveSubjectsByFilter(
      { ...nextDims, budgetType: BUDGET_TYPE_ALL },
      migrated
    );
    if (!saveResult.ok) {
      return { ok: false, reason: saveResult.reason || '차년도 과목 저장에 실패했습니다.' };
    }
    upsertHistoryForSubjects(nextDims.tenantCode || dims.tenantCode, migrated, nextDims.fiscalYear);
  }

  return {
    ok: true,
    nextDims,
    structureStatus: sourceStruct.status || '작성중',
    subjectCount: migrated.length,
    message: `${dims.fiscalYear}→${nextDims.fiscalYear} 과목구조·과목 ${migrated.length}건을 이관했습니다.`,
  };
}

/**
 * 사업 차년도이관: 구조 전체 + 사업(전체|선택)
 * @param {'all'|'selected'} mode
 * @param {string[]} selectedKeys - budgetType::projectCode
 */
export async function migrateProjectYear(dims, { userId, mode = 'all', selectedKeys = [] } = {}) {
  const fy = dims?.fiscalYear;
  if (!fy || !Number.isFinite(Number(fy))) {
    return { ok: false, reason: '회계년도가 올바르지 않습니다.' };
  }
  const sourceStruct = await loadProjectStructurePayload(dims);
  if (!sourceStruct?.rows?.length) {
    return { ok: false, reason: '이관할 사업구조가 없습니다. 먼저 구조를 저장하세요.' };
  }

  const structResult = await copyProjectStructureToNextYear(dims, { userId });
  if (!structResult.ok) return structResult;

  const nextDims = {
    ...structResult.nextDims,
    purpose: dims.purpose || PROJECT_PURPOSE,
  };

  const sourceAll = rebuildProjectPaths(await loadProjects(dims));
  let toMigrate = sourceAll;
  if (mode === 'selected') {
    if (!selectedKeys.length) {
      return { ok: false, reason: '이관할 사업을 선택하세요.' };
    }
    toMigrate = rebuildProjectPaths(expandProjectsWithAncestors(selectedKeys, sourceAll));
  }

  let added = 0;
  let skipped = 0;
  let finalRows;

  if (mode === 'all') {
    finalRows = rebuildProjectPaths(toMigrate.map((r) => ({ ...r })));
    added = finalRows.length;
    skipped = 0;
  } else {
    const existingNext = rebuildProjectPaths(await loadProjects(nextDims));
    const byUid = new Map(existingNext.filter((r) => r.projectUid).map((r) => [r.projectUid, r]));
    const byKey = new Set(existingNext.map(projectRowKey));
    const merged = [...existingNext];

    toMigrate.forEach((r) => {
      if (r.projectUid && byUid.has(r.projectUid)) {
        skipped += 1;
        return;
      }
      const k = projectRowKey(r);
      if (byKey.has(k)) {
        skipped += 1;
        return;
      }
      merged.push({ ...r });
      byKey.add(k);
      if (r.projectUid) byUid.set(r.projectUid, r);
      added += 1;
    });
    finalRows = rebuildProjectPaths(merged);
  }

  await saveProjects(nextDims, finalRows);
  upsertHistoryForProjects(nextDims.tenantCode || dims.tenantCode, finalRows, nextDims.fiscalYear);

  const modeLabel = mode === 'all' ? '전체' : '선택';
  return {
    ok: true,
    nextDims,
    structureStatus: sourceStruct.status || '작성중',
    mode,
    projectCount: toMigrate.length,
    added,
    skipped,
    message:
      mode === 'all'
        ? `${dims.fiscalYear}→${nextDims.fiscalYear} 사업구조·사업 ${added}건을 이관했습니다. (${modeLabel})`
        : `${dims.fiscalYear}→${nextDims.fiscalYear} 사업구조·사업 이관 (${modeLabel}): 추가 ${added}건` +
          (skipped ? `, 기존유지 ${skipped}건` : '') +
          (toMigrate.length !== added ? ` · 선택·상위 포함 ${toMigrate.length}건` : ''),
  };
}

export async function listProjectsForMigrate(dims) {
  return rebuildProjectPaths(await loadProjects(dims));
}
